import {PriorityIcon, Icon, Input, Flex} from '@priority-software/priority-style-react';
import React, {useRef} from 'react';
import texts from '../texts';
import {createDataTestId} from '../../../utils/dataTestId';

interface Props {
    searchQuery: string;
    onChangeQuery: (value: string) => void;
    inputClasses?: string;
    roundedCorners?: boolean;
    placeholder?: string;
    inputRef?: React.RefObject<HTMLInputElement>;
    scrollbarRef?: React.RefObject<any>; // Reference to the scrollbar component
}
const SearchInput = ({
    searchQuery,
    inputClasses,
    onChangeQuery,
    roundedCorners = false,
    placeholder,
    inputRef,
    scrollbarRef
}: Props) => {
    // Store the current scroll position before clearing the search
    const scrollPositionRef = useRef<number | null>(null);

    // Handle clearing the search while preserving scroll position
    const handleClearSearch = () => {
        // Get the scrollable element from the ref
        const scrollableElement = scrollbarRef?.current?.scrollerRef?.current;

        if (scrollableElement) {
            // Store the current scroll position
            scrollPositionRef.current = scrollableElement.scrollTop;

            // Clear the search
            onChangeQuery('');

            // Restore the scroll position after the DOM updates
            // Use multiple attempts with increasing delays to ensure it works
            const restoreScrollPosition = (attempts = 0) => {
                setTimeout(() => {
                    if (scrollableElement && scrollPositionRef.current !== null) {
                        scrollableElement.scrollTop = scrollPositionRef.current;

                        // If the scroll position wasn't restored correctly and we haven't tried too many times,
                        // try again with a longer delay
                        if (
                            scrollableElement.scrollTop !== scrollPositionRef.current &&
                            attempts < 3
                        ) {
                            restoreScrollPosition(attempts + 1);
                        }
                    }
                }, 50 * (attempts + 1)); // Increasing delay: 50ms, 100ms, 150ms
            };

            restoreScrollPosition();
        } else {
            // If we can't find the scroll container, just clear the search
            onChangeQuery('');
        }
    };

    return (
        <Input
            inputRef={inputRef}
            placeholder={placeholder || texts.searchInput}
            value={searchQuery || ''}
            componentClasses={inputClasses}
            onChange={e => onChangeQuery(e.target.value)}
            {...createDataTestId('analytics-search-input', true)}
            roundedCorners={roundedCorners}
            onFocus={e => e.target.select()}
        >
            <Flex inline>
                {searchQuery && <Icon icon={PriorityIcon.closeSmall} onClick={handleClearSearch} />}
                <Icon icon={PriorityIcon.searchThin} />
            </Flex>
        </Input>
    );
};

export default SearchInput;
